import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  STATUS_LABELS, STATUS_STYLE, TIER, BRAND_CLS, NOT_INTERESTED_REASONS, dateWithAgo, money,
} from "@/lib/format";
import MapExplorer from "@/components/MapExplorer";
import GarageCard from "@/components/GarageCard";
import { useConfirm } from "@/components/ConfirmProvider";
import AreaFilter from "@/components/AreaFilter";
import { useCheckin } from "@/components/CheckinProvider";
import { downloadCsv } from "@/lib/download";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Search, List, Map as MapIcon, Download, ArrowUpDown, Trash2, Plus,
  MessageSquare, MapPinCheck, ThumbsDown, Route as RouteIcon, Star, ChevronDown, X,
} from "lucide-react";

function ScoreCell({ score, tier }) {
  if (!tier) return <span className="text-xs font-bold text-[#9CA3AF] whitespace-nowrap">Not scored</span>;
  const t = TIER[tier] || TIER.skip;
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 h-9 rounded-md bg-[#111827] text-white flex items-center justify-center text-sm font-black shrink-0">{score ?? "—"}</div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${t.cls}`}>{t.label}</span>
    </div>
  );
}

function Google({ g }) {
  if (g.google_rating == null) return <span className="text-[#9CA3AF]">—</span>;
  return (
    <span className="flex items-center gap-1 whitespace-nowrap">
      <Star className="w-3 h-3 text-[#F59E0B]" /> {g.google_rating} ({g.google_review_count || 0})
    </span>
  );
}

export default function GarageListPanel({
  scope, title, subtitle, isManager, onOpenDetail, onAdd, extraAction, refreshKey,
}) {
  const isProspects = scope === "prospects";
  const confirm = useConfirm();
  const globalCheckin = useCheckin();
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});
  const [niGarage, setNiGarage] = useState(null); // not-interested dialog target
  const [niReason, setNiReason] = useState("");
  const [homeBase, setHomeBase] = useState(null);
  const [overlayMine, setOverlayMine] = useState(false);
  const [mineGarages, setMineGarages] = useState([]);

  const [filters, setFilters] = useState({
    search: "", tier: "", min_rating: "", min_reviews: "", category: "", status: "",
  });
  const [areaSel, setAreaSel] = useState([]); // multi-select areas
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [sort, setSort] = useState(
    isProspects ? { by: "opportunity_score", dir: "desc" } : { by: "last_visit_date", dir: "desc" }
  );

  const load = useCallback(async () => {
    try {
      const g = await api.get("/garages", { params: { scope, fields: "list" } });
      setGarages(g.data);
    } catch (e) { toast.error("Could not load garages"); }
    setLoading(false);
  }, [scope]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("jam:data", h);
    return () => window.removeEventListener("jam:data", h);
  }, [load]);

  useEffect(() => {
    api.get("/settings/home-base").then((r) => setHomeBase(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isProspects && overlayMine && mineGarages.length === 0) {
      api.get("/garages", { params: { scope: "mine", fields: "list" } }).then((r) => setMineGarages(r.data)).catch(() => {});
    }
  }, [isProspects, overlayMine, mineGarages.length]);

  const areaCounts = useMemo(() => {
    const m = {};
    garages.forEach((g) => { if (g.area) m[g.area] = (m[g.area] || 0) + 1; });
    return m;
  }, [garages]);
  const areas = useMemo(
    () => Array.from(new Set(garages.map((g) => g.area).filter(Boolean))).sort(),
    [garages]
  );

  const areaCore = (a) => (a ? a.split(",")[0].trim().toLowerCase() : "");
  const areaEmirate = (a) => (a && a.includes(",") ? a.split(",").pop().trim().toLowerCase() : "");
  const areaMatchesFE = (sel, gArea) => {
    if (gArea === sel) return true;
    const se = areaEmirate(sel), ge = areaEmirate(gArea);
    if (se && ge && se !== ge) return false;
    const s = areaCore(sel), g = areaCore(gArea);
    return !!g && (g === s || g.startsWith(s + " "));
  };
  const categories = useMemo(
    () => Array.from(new Set(garages.map((g) => g.category).filter(Boolean))).sort(),
    [garages]
  );

  const rows = useMemo(() => {
    let r = garages;
    const f = filters;
    if (f.search) r = r.filter((g) => g.name?.toLowerCase().includes(f.search.toLowerCase()));
    if (areaSel.length) r = r.filter((g) => areaSel.some((s) => areaMatchesFE(s, g.area)));
    if (isProspects) {
      if (f.tier) r = r.filter((g) => g.prospect_tier === f.tier);
      if (f.min_rating) r = r.filter((g) => (g.google_rating || 0) >= parseFloat(f.min_rating));
      if (f.min_reviews) r = r.filter((g) => (g.google_review_count || 0) >= parseInt(f.min_reviews));
      if (f.category) r = r.filter((g) => g.category === f.category);
      if (f.status) r = r.filter((g) => g.display_status === f.status);
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const by = sort.by;
    return [...r].sort((a, b) => {
      let va = a[by], vb = b[by];
      if (["last_visit_date", "last_order_date", "last_purchase_date"].includes(by)) {
        va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0;
      } else if (typeof va === "string" || typeof vb === "string") {
        va = (va || "").toLowerCase(); vb = (vb || "").toLowerCase();
        return va < vb ? -dir : va > vb ? dir : 0;
      } else { va = va || 0; vb = vb || 0; }
      return (va - vb) * dir;
    });
  }, [garages, filters, areaSel, sort, isProspects]);

  const selectable = rows;
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);
  useEffect(() => { setPage(1); }, [filters, areaSel, sort]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  const selectedIds = Object.keys(checked).filter((k) => checked[k]);
  const allChecked = selectable.length > 0 && selectable.every((r) => checked[r.id]);
  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const toggleAll = () => setChecked(allChecked ? {} : Object.fromEntries(selectable.map((r) => [r.id, true])));
  const selectGood = () => setChecked(Object.fromEntries(
    selectable.filter((r) => ["hot", "good"].includes(r.prospect_tier)).map((r) => [r.id, true])
  ));

  const bulkDelete = async () => {
    const n = selectedIds.length;
    if (!(await confirm({ title: `Delete ${n} garage${n !== 1 ? "s" : ""}?`, description: "This permanently removes the selected records and their visits/orders. This cannot be undone.", confirmText: `Delete ${n}`, destructive: true }))) return;
    try { const { data } = await api.post("/garages/bulk-delete", { ids: selectedIds }); toast.success(`Deleted ${data.deleted}`); setChecked({}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (g) => {
    if ((g.visit_count || 0) < 1) {
      toast.error("Record a visit before creating the WhatsApp group");
      return;
    }
    if (!(await confirm({ title: "Convert to My garages", description: `Move "${g.name}" to My garages? This marks the WhatsApp group as created.`, confirmText: "Convert" }))) return;
    try {
      await api.patch(`/garages/${g.id}`, { status: "group_created" });
      toast.success(`${g.name} moved to My garages`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNotInterested = async () => {
    if (!niReason) { toast.error("Pick a reason"); return; }
    try {
      await api.patch(`/garages/${niGarage.id}`, { status: "not_interested", not_interested_reason: niReason });
      toast.success(`${niGarage.name} marked not interested`);
      setNiGarage(null); setNiReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addToRoute = async (ids) => {
    if (ids.length === 0) { toast.error("Select some prospects first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/route/plans/add", { garage_ids: ids });
      toast.success(data.added > 0 ? `Added ${data.added} to today's route` : "Already in today's route");
      setChecked({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (g) => {
    if (!(await confirm({ title: "Delete garage", description: `Delete "${g.name}"? This cannot be undone.`, confirmText: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/garages/${g.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setSortBy = (col) => setSort((p) => p.by === col ? { by: col, dir: p.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "desc" });
  const Th = ({ col, children, className = "" }) => (
    <th onClick={() => setSortBy(col)} data-testid={`sort-${col}`}
      className={`px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-[#6B7280] cursor-pointer select-none hover:text-[#1E3A8A] ${className}`}>
      <span className="inline-flex items-center gap-1">{children}
        <ArrowUpDown className={`w-3 h-3 ${sort.by === col ? "text-[#1E3A8A]" : "opacity-40"}`} /></span>
    </th>
  );

  const toggleArea = (a) => setAreaSel((s) => s.includes(a) ? s.filter((x) => x !== a) : [...s, a]);

  return (
    <div data-testid={`garage-panel-${scope}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">{title}</h1>
          <p className="text-sm text-[#6B7280] mt-1">{subtitle} · {rows.length} garage{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-[#D1D5DB] overflow-hidden">
            <button onClick={() => setView("list")} data-testid="view-list"
              className={`h-11 px-3 text-sm font-bold flex items-center gap-1.5 ${view === "list" ? "bg-[#1E3A8A] text-white" : "bg-white text-[#374151]"}`}>
              <List className="w-4 h-4" /> List
            </button>
            <button onClick={() => setView("map")} data-testid="view-map"
              className={`h-11 px-3 text-sm font-bold flex items-center gap-1.5 ${view === "map" ? "bg-[#1E3A8A] text-white" : "bg-white text-[#374151]"}`}>
              <MapIcon className="w-4 h-4" /> Map
            </button>
          </div>
          {extraAction}
          {isManager && (
            <button onClick={() => downloadCsv("/export/garages.csv", "jam-garages.csv")} data-testid="export-garages"
              className="h-11 px-3 rounded-lg border border-[#D1D5DB] bg-white font-bold text-sm flex items-center gap-2">
              <Download className="w-4 h-4" /> CSV
            </button>
          )}
          {onAdd && (
            <button onClick={onAdd} data-testid="open-add-garage"
              className="h-11 px-4 rounded-lg bg-[#1E3A8A] text-white font-bold text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add garage
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input placeholder="Search by name…" value={filters.search} data-testid="filter-search"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full h-11 pl-9 pr-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
        </div>

        {/* Multi-select area (search + emirate grouping) */}
        <AreaFilter garages={garages} selected={areaSel} onChange={setAreaSel} testId="filter-area" />

        {isProspects && (
          <>
            <select value={filters.tier} data-testid="filter-tier" onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
              className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm">
              <option value="">All tiers</option>
              {Object.entries(TIER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filters.min_rating} data-testid="filter-min-rating" onChange={(e) => setFilters({ ...filters, min_rating: e.target.value })}
              className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm">
              <option value="">Any rating</option>
              <option value="3.5">3.5★+</option><option value="4">4★+</option><option value="4.5">4.5★+</option>
            </select>
            <select value={filters.min_reviews} data-testid="filter-min-reviews" onChange={(e) => setFilters({ ...filters, min_reviews: e.target.value })}
              className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm">
              <option value="">Any reviews</option>
              <option value="10">10+</option><option value="50">50+</option><option value="150">150+</option>
            </select>
            {categories.length > 0 && (
              <select value={filters.category} data-testid="filter-category" onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm max-w-[160px]">
                <option value="">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <select value={filters.status} data-testid="filter-status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm">
              <option value="">Any status</option>
              <option value="not_visited">Not visited</option>
              <option value="visited">Visited</option>
              <option value="not_interested">Not interested</option>
            </select>
          </>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-2.5 mb-4 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
          <span className="text-sm font-bold text-[#1E3A8A] px-1">{selectedIds.length} selected</span>
          {isProspects && (
            <button onClick={() => addToRoute(selectedIds)} disabled={busy} data-testid="bulk-add-route"
              className="h-10 px-3 rounded-md bg-[#1E3A8A] text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RouteIcon className="w-4 h-4" />} Add to route
            </button>
          )}
          {isProspects && (
            <button onClick={selectGood} data-testid="select-good"
              className="h-10 px-3 rounded-md border border-[#FCA5A5] text-[#B91C1C] font-bold text-sm">Select Hot + Good</button>
          )}
          {isManager && (
            <button onClick={bulkDelete} data-testid="bulk-delete"
              className="h-10 px-3 rounded-md bg-[#B91C1C] text-white font-bold text-sm flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete {selectedIds.length}</button>
          )}
          <button onClick={() => setChecked({})} data-testid="clear-selection"
            className="h-10 px-3 rounded-md border border-[#D1D5DB] font-bold text-sm">Clear</button>
        </div>
      )}

      {/* 4.1 — totals bar on My garages, selection-aware */}
      {!isProspects && !loading && view !== "map" && rows.length > 0 && (() => {
        const src = selectedIds.length ? rows.filter((r) => checked[r.id]) : rows;
        const t = src.reduce((a, g) => ({
          v: a.v + (g.visit_count || 0), o: a.o + (g.purchase_count || 0),
          val: a.val + (g.total_purchase_value || 0), c: a.c + (g.total_commission || 0),
        }), { v: 0, o: 0, val: 0, c: 0 });
        return (
          <div className="bg-[#1E3A8A] text-white rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-1" data-testid="mine-totals">
            <span className="text-xs font-bold uppercase tracking-wide text-white/70">{selectedIds.length ? `Selected (${src.length})` : `All (${src.length})`}</span>
            <span className="text-sm"><b className="text-base">{src.length}</b> garages</span>
            <span className="text-sm"><b className="text-base">{t.v}</b> visits</span>
            <span className="text-sm"><b className="text-base">{t.o}</b> orders</span>
            <span className="text-sm">Value <b className="text-base">{money(t.val)}</b></span>
            <span className="text-sm text-[#FCD34D]">Commission <b className="text-base">{money(t.c)}</b></span>
          </div>
        );
      })()}

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>
      ) : view === "map" ? (
        <div>
          {isProspects && (
            <label className="flex items-center gap-2 mb-3 text-sm font-medium text-[#374151] cursor-pointer" data-testid="explorer-overlay-toggle">
              <input type="checkbox" checked={overlayMine} onChange={(e) => setOverlayMine(e.target.checked)} />
              Show my garages too
            </label>
          )}
          <MapExplorer
            garages={[
              ...rows.map((g) => ({ ...g, _kind: isProspects ? "prospect" : "mine" })),
              ...(isProspects && overlayMine ? mineGarages.map((g) => ({ ...g, _kind: "mine" })) : []),
            ]}
            homeBase={homeBase}
            onOpenDetail={onOpenDetail}
            testId={`explorer-${scope}`}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-[#6B7280] bg-white border border-[#E5E7EB] rounded-lg">
          <p className="font-bold text-[#111827]">Nothing here yet</p>
          <p className="text-sm">{scope === "mine" ? "Garages appear here once you create their WhatsApp group." : "Scan an area or add a garage to build your prospect list."}</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="mobile-cards">
            {pageRows.map((g) => (
              <GarageCard key={g.id} g={g} showBadge={isProspects} onOpen={onOpenDetail} onCheckin={globalCheckin}
                onConvert={convert} onOrder={(x) => onOpenDetail(x.id)} onNotInterested={(x) => { setNiGarage(x); setNiReason(""); }} />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="garages-table">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  {isProspects ? (
                    <tr>
                      <th className="px-3 py-2.5 w-10"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="check-all" /></th>
                      <Th col="opportunity_score">Prospect score</Th>
                      <Th col="name">Place</Th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Why</th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Brand</th>
                      <Th col="google_rating">Google</Th>
                      <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Status</th>
                      <Th col="visit_count">Visits</Th>
                      <Th col="last_visit_date">Last visit</Th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-3 py-2.5 w-10"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="check-all" /></th>
                      <Th col="name">Garage</Th>
                      <Th col="area">Area</Th>
                      <Th col="last_visit_date">Last visit</Th>
                      <Th col="visit_count">Visits</Th>
                      <Th col="purchase_count">Orders</Th>
                      <Th col="total_purchase_value">Order value</Th>
                      <Th col="last_order_date">Last order</Th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {pageRows.map((g) => (
                    <tr key={g.id} onClick={() => onOpenDetail(g.id)} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" data-testid={`garage-row-${g.id}`}>
                      {isProspects ? (
                        <>
                          <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={!!checked[g.id]} onChange={() => toggle(g.id)} data-testid={`check-${g.id}`} /></td>
                          <td className="px-3 py-2.5"><ScoreCell score={g.opportunity_score} tier={g.prospect_tier} /></td>
                          <td className="px-3 py-2.5 cursor-pointer" onClick={() => onOpenDetail(g.id)} data-testid={`open-detail-${g.id}`}>
                            <div className="font-bold text-[#111827] hover:text-[#1E3A8A]">{g.name}</div>
                            <div className="text-xs text-[#6B7280]">{g.area}</div>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-[#374151] max-w-[240px]">{g.prospect_reason || "—"}</td>
                          <td className="px-3 py-2.5">
                            {g.brand_focus && <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${BRAND_CLS[g.brand_focus] || BRAND_CLS.unknown}`}>{g.brand_focus}</span>}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-[#374151]"><Google g={g} /></td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${STATUS_STYLE[g.display_status]}`}>{STATUS_LABELS[g.display_status]}</span>
                          </td>
                          <td className="px-3 py-2.5 text-sm font-bold text-[#111827]">{g.visit_count || 0}</td>
                          <td className="px-3 py-2.5 text-sm text-[#374151] whitespace-nowrap">{dateWithAgo(g.last_visit_date, "never")}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => globalCheckin(g)} data-testid={`checkin-${g.id}`} title="Check in now"
                                className="h-9 px-2.5 rounded-md border border-[#93C5FD] text-[#1E3A8A] text-xs font-bold flex items-center gap-1.5 whitespace-nowrap">
                                <MapPinCheck className="w-3.5 h-3.5" /> Check in
                              </button>
                              <button onClick={() => convert(g)} data-testid={`convert-${g.id}`} title="Move to My garages (WhatsApp group created)"
                                className="h-9 px-2.5 rounded-md bg-[#047857] text-white text-xs font-bold flex items-center gap-1.5">
                                <MessageSquare className="w-3.5 h-3.5" /> Convert
                              </button>
                              {g.status !== "not_interested" && (
                                <button onClick={() => { setNiGarage(g); setNiReason(""); }} data-testid={`not-interested-${g.id}`} title="Mark not interested"
                                  className="p-2 text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEE2E2] rounded-md">
                                  <ThumbsDown className="w-4 h-4" />
                                </button>
                              )}
                              {isManager && (
                                <button onClick={() => remove(g)} data-testid={`delete-${g.id}`}
                                  className="p-2 text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEE2E2] rounded-md">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={!!checked[g.id]} onChange={() => toggle(g.id)} data-testid={`check-${g.id}`} /></td>
                          <td className="px-3 py-2.5 cursor-pointer" onClick={() => onOpenDetail(g.id)} data-testid={`open-detail-${g.id}`}>
                            <div className="font-bold text-[#111827] hover:text-[#1E3A8A]">{g.name}</div>
                          </td>
                          <td className="px-3 py-2.5 text-sm text-[#374151]">{g.area}</td>
                          <td className="px-3 py-2.5 text-sm text-[#374151] whitespace-nowrap">{dateWithAgo(g.last_visit_date, "never")}</td>
                          <td className="px-3 py-2.5 text-sm font-bold text-[#111827]">{g.visit_count || 0}</td>
                          <td className="px-3 py-2.5 text-sm font-bold text-[#111827]">{g.purchase_count || 0}</td>
                          <td className="px-3 py-2.5 text-sm font-bold text-[#111827]">{money(g.total_purchase_value)}</td>
                          <td className="px-3 py-2.5 text-sm text-[#374151] whitespace-nowrap">{dateWithAgo(g.last_order_date, "none")}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => globalCheckin(g)} data-testid={`checkin-${g.id}`} title="Check in now"
                                className="h-9 px-2.5 rounded-md border border-[#93C5FD] text-[#1E3A8A] text-xs font-bold flex items-center gap-1.5 whitespace-nowrap">
                                <MapPinCheck className="w-3.5 h-3.5" /> Check in
                              </button>
                              {isManager && (
                                <button onClick={() => remove(g)} data-testid={`delete-${g.id}`}
                                  className="p-2 text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEE2E2] rounded-md">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pager */}
          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-3 mt-4" data-testid="garage-pager">
              <span className="text-sm text-[#6B7280]">
                Showing <b className="text-[#111827]">{(page - 1) * PAGE_SIZE + 1}</b>–<b className="text-[#111827]">{Math.min(page * PAGE_SIZE, rows.length)}</b> of <b className="text-[#111827]">{rows.length}</b>
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pager-prev"
                  className="h-10 px-4 rounded-md border border-[#D1D5DB] bg-white font-bold text-sm disabled:opacity-40">Prev</button>
                <span className="text-sm font-bold text-[#374151] px-1" data-testid="pager-status">{page} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="pager-next"
                  className="h-10 px-4 rounded-md border border-[#D1D5DB] bg-white font-bold text-sm disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Not interested reason dialog */}
      <Dialog open={!!niGarage} onOpenChange={(v) => { if (!v) { setNiGarage(null); setNiReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="list-ni-dialog">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">Mark not interested</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280] -mt-2">Why isn't <b>{niGarage?.name}</b> a fit? This helps us learn the market.</p>
          <div className="space-y-2 mt-1">
            {NOT_INTERESTED_REASONS.map((r) => (
              <label key={r.value} data-testid={`ni-reason-${r.value}`}
                className={`flex items-center gap-3 h-12 px-3 rounded-lg border cursor-pointer ${niReason === r.value ? "border-[#1E3A8A] bg-[#EEF2FF]" : "border-[#E5E7EB]"}`}>
                <input type="radio" name="ni-reason" checked={niReason === r.value} onChange={() => setNiReason(r.value)} />
                <span className="font-medium text-sm text-[#111827]">{r.label}</span>
              </label>
            ))}
          </div>
          <button onClick={saveNotInterested} data-testid="ni-save"
            className="w-full h-12 rounded-lg bg-[#B91C1C] text-white font-bold flex items-center justify-center gap-2 mt-2">
            <ThumbsDown className="w-4 h-4" /> Mark not interested
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
